import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We received your message and will get back soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-zinc-600 focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] outline-none transition";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-sm uppercase tracking-[0.25em] text-zinc-500">
            Contact
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4">
            Get in <span className="text-gradient">touch</span>
          </h2>
          <p className="mt-5 text-zinc-400">
            Questions? Stop by our store or send us a message.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="glass rounded-3xl p-8 md:p-10 space-y-5"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <input
              data-testid="contact-name-input"
              className={inputCls}
              placeholder="Name *"
              value={form.name}
              onChange={update("name")}
            />
            <input
              data-testid="contact-email-input"
              type="email"
              className={inputCls}
              placeholder="Email *"
              value={form.email}
              onChange={update("email")}
            />
          </div>
          <input
            data-testid="contact-phone-input"
            className={inputCls}
            placeholder="Phone"
            value={form.phone}
            onChange={update("phone")}
          />
          <textarea
            data-testid="contact-message-input"
            rows={5}
            className={inputCls + " resize-none"}
            placeholder="Message *"
            value={form.message}
            onChange={update("message")}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00e5ff] text-black font-semibold hover:shadow-[0_0_32px_rgba(0,229,255,0.5)] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Sending...
              </>
            ) : (
              <>
                <Send size={18} /> Send Message
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
